import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { RootStore } from 'stores/rootStore';
import { ApiStore } from 'stores/apiStore';
import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';

import Button from '@material-ui/core/Button';

import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import Deposits from 'pages/Accounts/Edit/components/Deposits';
import Chart from 'pages/Accounts/Edit/Plan/Chart/Chart';
import BucketSection from 'pages/Accounts/Edit/Plan/BucketSections';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import AccountInfo from '../components/AccountInfo';
import { Currency } from 'enums';
import { chartTooltipData, chart } from 'helpers/chart';

interface Props {
  rootStore?: RootStore;
  apiStore?: ApiStore;
  detailStore: AccountDetailStore;
  accountId: string;
  userMode?: boolean;
  currency: Currency;
  handleDepositModal: (open: boolean) => void;
  refreshTimestamp?: string;
}

const Dashboard: React.FC<Props> = ({
  rootStore,
  apiStore,
  detailStore,
  currency,
  accountId,
  handleDepositModal,
  userMode,
  refreshTimestamp,
}) => {
  const { t } = useTranslation();
  const [store] = useState(new WorkflowPlanStore(rootStore as RootStore, accountId, true, userMode));
  const termination = !userMode && detailStore.canTerminateContract;
  const [terminationModalOpen, setTerminationModalOpen] = useState(false);

  useEffect(() => {
    !termination && store.fetchAccountPlan();
  }, [store, refreshTimestamp, termination]);

  const refreshPlan = () => {};

  //console.log('render Dashboard',Date.now());
  return termination ? (
    <div style={{ padding: '8px 0', textAlign: 'center' }}>
      <Button variant="contained" color={'default'} onClick={() => setTerminationModalOpen(true)}>
        {t('accountDetail_terminate')}
      </Button>
      <ConfirmDialog
        noHeader
        isOpen={terminationModalOpen}
        confirm={async () => {
          await detailStore.terminateAccount();
          setTerminationModalOpen(false);
        }}
        close={() => setTerminationModalOpen(false)}
      >
        {t('accountDetail_terminate_info')}
      </ConfirmDialog>
    </div>
  ) : store.data ? (
    <>
      <AccountInfo store={detailStore} userMode={!!userMode} />

      <Deposits
        accountId={accountId}
        currency={currency}
        readOnly
        viewMode={ViewMode.DETAIL}
        data={store.data.depositData}
        refresh={refreshPlan}
        planStore={store}
        dashboard
        handleDepositModal={handleDepositModal}
        userMode={userMode}
        mode={store.data.mode}
        plan
        locked={detailStore.locked}
      />

      {store.chartComputedData.default === false && (
        <Chart
          planData={store.data}
          data={chart(store.data, false, store.data.externalProductData?.externalProducts)}
          metaData={store.chartComputedData}
          chartTooltipData={chartTooltipData(store.data, false, store.data.externalProductData?.externalProducts)}
          currency={currency}
          stacking="normal"
          accountId={accountId}
          store={store}
          readOnly
          dashboard
          userMode={userMode}
        />
      )}

      <BucketSection
        store={store}
        accountId={accountId}
        currency={currency}
        userMode={userMode}
        detailMode
        readOnly
        brief
      />
    </>
  ) : null;
};

export default React.memo(inject('rootStore', 'apiStore')(observer(Dashboard)));
